import {
  createApi,
  fetchBaseQuery,
  BaseQueryFn,
  FetchArgs,
  retry,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react';
import { setShowRecaptchaV2 } from '@/features/paymentProcess/transactionSlice';

const baseUrl = import.meta.env.VITE_PAYMENTS_SERVICE_URL;

const baseQuery = fetchBaseQuery({
  baseUrl,
});

interface PossibleRecaptchaErrorData {
  data?: {
    challenge?: boolean
    failure?: boolean
  }
}

function shouldRetryOnlyWhenNoHttpResponse(error: FetchBaseQueryError, attempt: number): boolean {
  const maxRetries = 3;
  if (attempt > maxRetries) return false;

  return error.status === 'FETCH_ERROR' || error.status === 'TIMEOUT_ERROR';
}

const baseQueryWithRecaptcha: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (args, api, extraOptions) => {
  const result = await baseQuery(args, api, extraOptions);
  if (result.error) {
    const error = result.error;
    if (error.status === 401) {
      const errorData = error.data as PossibleRecaptchaErrorData;
      if (errorData?.data?.challenge ?? errorData?.data?.failure) {
        api.dispatch(setShowRecaptchaV2(true));
      }
    }
  }

  return result;
};

const baseQueryWithRecaptchaAndRetry: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = retry(
  async (args: string | FetchArgs, api, extraOptions) => {
    const result = await baseQueryWithRecaptcha(args, api, extraOptions);

    return result;
  },
  {
    retryCondition: (error, _args, { attempt }) => shouldRetryOnlyWhenNoHttpResponse(error as FetchBaseQueryError, attempt),
  }
);

export const paymentsApi = createApi({
  reducerPath: 'paymentsApi',
  baseQuery: baseQueryWithRecaptchaAndRetry,
  endpoints: () => ({}),
});
